import React, { useState, useRef, useCallback } from 'react';
import { Box, Button, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

// Styles pour la zone de drop
const DropZone = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'border .3s ease-in-out',
  backgroundColor: theme.palette.background.default,
  '&:hover': {
    borderColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.action.hover,
  },
}));

export interface CSVFileUploaderProps {
  /**
   * Callback appelé lorsqu'un fichier est sélectionné
   */
  onFileSelected: (file: File) => void;
  
  /**
   * Types de fichiers acceptés
   * @default ['.csv', 'text/csv', 'application/vnd.ms-excel']
   */
  acceptedFileTypes?: string[];
  
  /**
   * Taille maximale du fichier en octets
   * @default 10485760 (10 Mo)
   */
  maxFileSize?: number;
  
  /**
   * Indique si le composant est en cours de chargement
   * @default false
   */
  loading?: boolean;
  
  /**
   * Message d'erreur à afficher
   */
  error?: string;
}

/**
 * Composant pour télécharger un fichier CSV
 * Supporte le glisser-déposer et la sélection via un bouton
 */
const CSVFileUploader: React.FC<CSVFileUploaderProps> = ({
  onFileSelected,
  acceptedFileTypes = ['.csv', 'text/csv', 'application/vnd.ms-excel'],
  maxFileSize = 10 * 1024 * 1024, // 10 Mo par défaut
  loading = false,
  error,
}): React.ReactElement => {
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Fonction pour valider le fichier
  const validateFile = useCallback((file: File): boolean => {
    // Vérifier le type de fichier
    const fileType = file.type;
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    const isValidType = acceptedFileTypes.includes(fileType) || 
                        acceptedFileTypes.includes(fileExtension);
    
    if (!isValidType) {
      setFileError(`Type de fichier non supporté. Types acceptés: ${acceptedFileTypes.join(', ')}`);
      return false;
    }
    
    // Vérifier la taille du fichier
    if (file.size > maxFileSize) {
      setFileError(`Fichier trop volumineux. Taille maximale: ${maxFileSize / 1024 / 1024} Mo`);
      return false;
    }
    
    setFileError(null);
    return true;
  }, [acceptedFileTypes, maxFileSize]);
  
  // Gestionnaire pour le glisser-déposer
  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);
  
  // Gestionnaire pour le dépôt de fichier
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        onFileSelected(file);
      }
    }
  }, [validateFile, onFileSelected]);
  
  // Gestionnaire pour la sélection de fichier via le bouton
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        onFileSelected(file);
      }
    }
  }, [validateFile, onFileSelected]);
  
  // Gestionnaire pour le clic sur la zone de drop
  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);
  
  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      {/* Zone de drop */}
      <DropZone
        elevation={0}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
        sx={{
          borderColor: dragActive ? 'primary.dark' : 'primary.main',
          backgroundColor: dragActive ? 'action.hover' : 'background.default',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptedFileTypes.join(',')}
          onChange={handleChange}
          style={{ display: 'none' }}
        />
        
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          Glissez-déposez votre fichier CSV ici
        </Typography>
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          ou
        </Typography>
        
        <Button
          variant="contained"
          component="span"
          disabled={loading}
          sx={{ mt: 1 }}
        >
          Sélectionner un fichier
        </Button>
        
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Types acceptés: {acceptedFileTypes.join(', ')} - Taille max: {maxFileSize / 1024 / 1024} Mo
        </Typography>
        
        {loading && (
          <CircularProgress sx={{ mt: 2 }} />
        )}
      </DropZone>
      
      {/* Affichage du fichier sélectionné */}
      {selectedFile && !fileError && !error && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Fichier sélectionné: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} Ko)
        </Alert>
      )}
      
      {/* Affichage des erreurs */}
      {(fileError || error) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {fileError || error}
        </Alert>
      )}
    </Box>
  );
};

export default CSVFileUploader;